import gsap from 'gsap';
import { nextTick, ref, watch } from 'vue';

import { useFocusManagement } from './use-focus-management';

/**
 * @typedef {Object} HeaderMobileFocusOptions
 * @property {import('vue').Ref<boolean>} isMobileOpen - Reactive ref indicating if the mobile menu is open.
 * @property {() => HTMLElement | null} getMobileMenuElement - Function that returns the main mobile menu container element.
 * @property {() => HTMLElement | null} getMobileTriggerElement - Function that returns the button element that toggles the mobile menu.
 */

/**
 * Manages focus trapping and restoration for the mobile header menu.
 * It orchestrates useFocusManagement based on the mobile menu's open state.
 *
 * @param {HeaderMobileFocusOptions} options
 */
export function useHeaderMobileFocus(options) {
  const { getMobileMenuElement, getMobileTriggerElement, isMobileOpen } = options;

  const menuContainerRef = ref(null);
  const triggerElementRef = ref(null);
  const isFocusActive = ref(false);

  // --- Focus Management Setup ---
  const { focusFirstElement } = useFocusManagement({
    containerRef: menuContainerRef,
    focusFirstOnActivate: true,
    focusTriggerElementRef: triggerElementRef,
    isActive: isFocusActive,
    onElementActivated: null,
    trapFocus: true,
  });

  // --- Watchers ---

  // Watch for the menu opening/closing to activate/deactivate focus management
  watch(
    isMobileOpen,
    (newIsOpen, oldIsOpen) => {
      if (newIsOpen) {
        const menuElement = getMobileMenuElement();
        const triggerElement = getMobileTriggerElement();

        if (menuElement && triggerElement) {
          menuContainerRef.value = menuElement;
          triggerElementRef.value = triggerElement;
          nextTick(() => {
            isFocusActive.value = true;
            gsap.delayedCall(150, () => {
              if (isFocusActive.value) {
                focusFirstElement();
              }
            });
          });
        }
      } else if (oldIsOpen) {
        isFocusActive.value = false;
        gsap.delayedCall(0, () => {
          menuContainerRef.value = null;
          triggerElementRef.value = null;
        });
      }
    },
    { flush: 'post' },
  );
}
